// docs/26 — reading a `Survey` (fk_sim::field) for the board and the HUD. Nothing here prices a
// step or decides a route: the sim did that; this only follows the survey's back-pointers and
// puts its numbers into words.
import type { FieldClass, Survey, Tile } from './types';

export interface FieldRoute { tiles: Tile[]; costs: number[]; cost: number; entangled: boolean }

const key = (t: Tile): string => `${t.x},${t.y}`;

/** `6` points at 4 a pace → `1½` (the sim's `field::paces`). */
export function paces(points: number, pace = 4): string {
  const whole = Math.floor(points / pace);
  const frac = ['', '¼', '½', '¾'][((points % pace) + pace) % pace] ?? '';
  if (whole === 0) return frac || '0';
  return `${whole}${frac}`;
}

/** The survey's route to `to` (start excluded), or `undefined` when the unit cannot stop there. */
export function fieldRoute(f: Survey, to: Tile): FieldRoute | undefined {
  // back-pointers may run through squares the unit can cross but not stop on (a friend's)
  const byTile = new Map(f.reach.map((r) => [key(r.tile), r]));
  const end = byTile.get(key(to));
  if (!end || !end.stand) return undefined;
  const tiles: Tile[] = [];
  let cur: Tile = to;
  for (let guard = 0; guard < 4096 && (cur.x !== f.from.x || cur.y !== f.from.y); guard++) {
    tiles.push(cur);
    const r = byTile.get(key(cur));
    if (!r) break;
    cur = r.prev;
  }
  tiles.reverse();
  const costs: number[] = [];
  let before = 0;
  for (const t of tiles) {
    const c = byTile.get(key(t))?.cost ?? before + f.pace;
    costs.push(c - before);
    before = c;
  }
  return { tiles, costs, cost: end.cost, entangled: end.entangled };
}

/** The class of ground under `t`, priced for the surveyed unit. */
export function fieldClassAt(f: Survey, t: Tile, w: number): FieldClass | undefined {
  if (t.x < 0 || t.y < 0 || t.x >= w) return undefined;
  const i = f.ground[t.y * w + t.x];
  return i === undefined ? undefined : f.legend[i];
}

/** What a square costs this unit to enter, in words: `2 paces`, `cannot enter` (docs/93 AQ10: an Aquatic unit's land). */
export function classCost(c: FieldClass, pace = 4): string {
  if (c.cost === null) return 'cannot enter';
  const p = paces(c.cost, pace);
  return `${p} ${c.cost === pace ? 'pace' : 'paces'}`;
}

/**
 * The chip under the cursor while planning a Move: the walk's cost against the budget and whether
 * it ends gripped; off the reach, what the ground is and why it is out of reach.
 */
export function fieldTileNote(f: Survey, t: Tile, route: FieldRoute | undefined, unknown = false): string | undefined {
  const c = fieldClassAt(f, t, f.w);
  // docs/139 WD5: a never-seen square is walked into on hope; its ground is not named
  const ground = unknown ? 'Into the unknown' : c ? c.label : undefined;
  if (t.x === f.from.x && t.y === f.from.y) return undefined;
  if (route) {
    const parts = [`${paces(route.cost, f.pace)} of ${paces(f.budget, f.pace)}`];
    if (ground) parts.unshift(ground);
    if (route.entangled) parts.push('held in their reach');
    return parts.join(' · ');
  }
  if (!c) return undefined;
  if (c.cost === null) return `${c.label} · cannot enter`;
  if (f.zones.some((z) => z.tile.x === t.x && z.tile.y === t.y)) return `${c.label} · beyond their reach`;
  return `${c.label} · ${classCost(c, f.pace)} · too far`;
}
